import React, { MouseEventHandler } from 'react';
import { ConditionClassification } from '../interfaces/ProcessObject';
import styled from 'styled-components';
import {
  CertifiedIcon,
  ButtonSmallText,
  WarningIcon,
  Subtitle,
  InfoIcon
} from '@kvdbil/components';
import { useTranslation } from '~/Locale';
import { getConditionText } from '~/App/views/ObjectPage/helpers';

type ConditionStatusStyleTypes = {
  $condition: ConditionClassification;
  $variation?: 'CARD' | 'OBJECT_PAGE';
};

const Container = styled.div<ConditionStatusStyleTypes>`
  position: relative;
  border-radius: 6.25rem;
  display: flex;
  align-items: center;
  cursor: pointer;

  background-color: ${({ theme, $condition }) =>
    $condition === 'REPAIR_OBJECT'
      ? theme.colors.tertiary.mainAlt
      : theme.colors.primary.lighter2};

  color: ${({ theme, $condition }) =>
    $condition === 'REPAIR_OBJECT'
      ? theme.colors.text.light
      : theme.colors.text.dark};

  padding: ${({ $variation }) =>
    $variation === 'CARD'
      ? '0.375rem 0.75rem 0.375rem 2.125rem'
      : '0.75rem 1.5rem 0.75rem 5rem'};
`;

const IconContainer = styled.div<ConditionStatusStyleTypes>`
  border-radius: 50%;
  margin-right: 0.5rem;
  position: absolute;
  left: -1;

  background-color: ${({ theme, $condition }) =>
    $condition === 'REPAIR_OBJECT'
      ? theme.colors.tertiary.mainAlt
      : theme.colors.primary.main};

  border: ${({ $variation, theme }) =>
    `${$variation === 'CARD' ? '2px' : '4px'} solid ${
      theme.colors.background.light
    }`};

  padding: ${({ $variation }) =>
    $variation === 'CARD' ? '0.25rem' : '0.875rem'};

  svg {
    height: ${({ $variation }) =>
      $variation === 'CARD' ? '1rem' : '1.625rem'};
    width: ${({ $variation }) => ($variation === 'CARD' ? '1rem' : '1.625rem')};
  }
`;

const StyledInfoIcon = styled(InfoIcon)<ConditionStatusStyleTypes>`
  margin-left: 0.5rem;
  color: ${({ theme, $condition }) =>
    $condition === 'REPAIR_OBJECT'
      ? theme.colors.text.light
      : theme.colors.grayscaleNeutral.dark5};
`;

export const ConditionStatus = ({
  condition,
  variation,
  onClick
}: {
  condition: ConditionClassification;
  variation: 'CARD' | 'OBJECT_PAGE';
  onClick?: MouseEventHandler<HTMLDivElement>;
}) => {
  const { t } = useTranslation();

  if (!condition || condition === 'STANDARD') return null;

  return (
    <Container $condition={condition} $variation={variation} onClick={onClick}>
      <IconContainer $condition={condition} $variation={variation}>
        {condition === 'REPAIR_OBJECT' ? <WarningIcon /> : <CertifiedIcon />}
      </IconContainer>
      {variation === 'CARD' ? (
        <ButtonSmallText>{getConditionText(condition, t)}</ButtonSmallText>
      ) : (
        <Subtitle>{getConditionText(condition, t)}</Subtitle>
      )}
      {variation === 'OBJECT_PAGE' && (
        <StyledInfoIcon $condition={condition} width="1.2rem" height="1.2rem" />
      )}
    </Container>
  );
};
